import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../../../store";
import { fetchCarQuickAddOptions } from "../../infrastructure/slices/requestQuickAddSlice";
import { CarQuickAddOption } from "../../infrastructure/slices/requestQuickAddSlice";
import LicensePlate from "license-plate";
import licencePlateSelectBg from "../../../general/fields/modules/commons/licencePlateSelectField/assets/licencePlateSelectBg.svg";

interface LicensePlateOption {
  label: string;
  value: string;
  __isNew__?: boolean;
}

interface LicensePlateSelectFieldProps {
  value: string | null;
  onChange: (license: string | null, isCustom: boolean) => void;
  placeholder?: string;
}

function safeTrim(val: unknown): string {
  return typeof val === "string" ? val.trim() : "";
}

export const LicensePlateSelectField: React.FC<
  LicensePlateSelectFieldProps
> = ({ value, onChange, placeholder }) => {
  const { t } = useTranslation("common");

  const dispatch = useDispatch<AppDispatch>();
  const carOptions: CarQuickAddOption[] = useSelector(
    (state: RootState) => state.requestQuickAdd.carQuickAddOptions,
  );

  // Local state to store current search results for validation
  const [currentSearchResults, setCurrentSearchResults] = useState<
    CarQuickAddOption[]
  >([]);

  // Load license plate options directly from the thunk response
  const loadLicensePlateOptions = async (
    inputValue: string,
  ): Promise<LicensePlateOption[]> => {
    const cleanedInput = inputValue.replace(/[^A-Z0-9]/gi, "").toLowerCase();
    const result = await dispatch(
      fetchCarQuickAddOptions(cleanedInput),
    ).unwrap();

    // Update local state with search results
    setCurrentSearchResults(result);

    return result.map((option) => {
      const hasLicence = safeTrim(option.licence) !== "";
      const label = hasLicence
        ? safeTrim(option.licence)
        : (option.additional_info ?? "");
      return { label, value: option.id };
    });
  };

  // Check if a license plate already exists in the current search results
  const licenseAlreadyExists = useCallback(
    (inputValue: string): boolean => {
      const cleanInput = inputValue.replace(/[^A-Z0-9]/gi, "").toUpperCase();

      // Check if the license exists in current search results first
      const existsInCurrentResults = currentSearchResults.some((option) => {
        const hasLicence = safeTrim(option.licence) !== "";
        const label = hasLicence
          ? safeTrim(option.licence)
          : (option.additional_info ?? "");
        const cleanOptionLabel = (label as string)
          .replace(/[^A-Z0-9]/gi, "")
          .toUpperCase();
        return cleanOptionLabel === cleanInput;
      });

      // If found in current search results, return true
      if (existsInCurrentResults) {
        return true;
      }

      // Otherwise check carOptions as fallback
      return carOptions.some((option) => {
        const hasLicence = safeTrim(option.licence) !== "";
        const label = hasLicence
          ? safeTrim(option.licence)
          : (option.additional_info ?? "");
        const cleanOptionLabel = (label as string)
          .replace(/[^A-Z0-9]/gi, "")
          .toUpperCase();
        return cleanOptionLabel === cleanInput;
      });
    },
    [currentSearchResults, carOptions],
  );

  // Validate if input is a valid license plate (at least 2 alphanumeric characters)
  const isValidLicensePlate = (input: string): boolean => {
    return /^[A-Z0-9]{2,}$/i.test(input.replace(/[^A-Z0-9]/gi, ""));
  };

  // Handle license plate selection/input
  const handleLicensePlateChange = (
    option: LicensePlateOption | null,
    { action }: { action: string },
  ) => {
    if (action === "input-change") return; // Don't handle input changes, only selections

    if (option) {
      if (option.__isNew__) {
        // Custom input - new license plate
        const cleanLicense = option.value
          .replace(/[^A-Z0-9]/gi, "")
          .toUpperCase();
        if (cleanLicense.length >= 2 && isValidLicensePlate(cleanLicense)) {
          onChange(cleanLicense, true); // isCustom = true, pass the license plate
        }
      } else {
        // Existing car selected - pass the car ID for existing cars
        onChange(option.value, false); // isCustom = false, pass the car ID
      }
    } else {
      // Clear selection
      onChange(null, false);
    }
  };

  // Create display value - for existing cars, we need to show the license plate but store the ID
  const getDisplayValue = () => {
    if (!value) return null;

    // First check current search results, then fallback to carOptions
    const allOptions = [...currentSearchResults, ...carOptions];
    const existingOption = allOptions.find((option) => option.id === value);

    if (existingOption) {
      const hasLicence = safeTrim(existingOption.licence) !== "";
      const label = hasLicence
        ? safeTrim(existingOption.licence)
        : (existingOption.additional_info ?? "");
      const plate = new LicensePlate(label);
      return {
        label: plate.valid() ? plate.pretty() : label,
        value: existingOption.id,
      } as LicensePlateOption;
    }
    if (/^\d+$/.test(value) && value.length < 6) {
      return {
        label: `Loading...`,
        value: value,
      } as LicensePlateOption;
    } else {
      const plate = new LicensePlate(value);
      return {
        label: plate.valid() ? plate.pretty() : value,
        value: value,
      } as LicensePlateOption;
    }
  };

  return (
    <AsyncCreatableSelect<LicensePlateOption>
      cacheOptions={false} // Disable caching to ensure fresh results
      loadOptions={loadLicensePlateOptions}
      onChange={handleLicensePlateChange}
      value={getDisplayValue()}
      isClearable
      placeholder={
        placeholder || t("Type or select a license plate (6+ characters)...")
      }
      filterOption={() => true} // Disable client-side filtering, rely on server-side search
      styles={{
        singleValue: (base) => ({
          ...base,
          background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
          width: "112px",
          height: "23px",
          display: "flex",
          flexDirection: "row-reverse",
          justifyContent: "flex-end",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: 500,
          color: "#000",
          paddingLeft: "20px",
          letterSpacing: "2px",
        }),
        multiValue: (base) => ({
          ...base,
          background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
          width: "112px",
          height: "23px",
          display: "flex",
          flexDirection: "row-reverse",
          justifyContent: "flex-end",
          alignItems: "center",
        }),
        multiValueRemove: () => ({
          color: "#fff",
          height: "23px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "15px",
        }),
        multiValueLabel: () => ({
          fontSize: "14px",
          fontWeight: 500,
          color: "#000",
          height: "auto",
          width: "100%",
          paddingLeft: "5px",
          letterSpacing: "2px",
        }),
      }}
      noOptionsMessage={({ inputValue }) => {
        const cleanInput = inputValue.replace(/[^A-Z0-9]/gi, "").toUpperCase();

        if (licenseAlreadyExists(inputValue)) {
          return t("license_plate_exists_select_from_results", { cleanInput });
        }

        if (cleanInput.length >= 2 && isValidLicensePlate(cleanInput)) {
          return t("press_enter_to_use_new_license_plate", { cleanInput });
        }

        return cleanInput.length === 0
          ? t("Type to search existing license plates or enter a new one")
          : t(
              "License plate must be at least 2 characters (letters and numbers only)",
            );
      }}
      formatCreateLabel={(inputValue) => {
        const cleanInput = inputValue.replace(/[^A-Z0-9]/gi, "").toUpperCase();
        if (licenseAlreadyExists(inputValue)) {
          return t("license_plate_exists_select_from_above", { cleanInput });
        }
        // Format as proper license plate for display
        const formattedPlate = new LicensePlate(cleanInput);
        const displayPlate = formattedPlate.valid()
          ? formattedPlate.pretty()
          : cleanInput;
        return `${displayPlate} ${t("(add new)")}`;
      }}
      isValidNewOption={(inputValue) => {
        const cleanInput = inputValue.replace(/[^A-Z0-9]/gi, "");
        // Prevent creating new option if license plate already exists or is invalid
        return (
          cleanInput.length >= 2 &&
          isValidLicensePlate(cleanInput) &&
          !licenseAlreadyExists(inputValue)
        );
      }}
      getNewOptionData={(inputValue) => {
        const cleanInput = inputValue.replace(/[^A-Z0-9]/gi, "").toUpperCase();
        const formattedPlate = new LicensePlate(cleanInput);
        const displayPlate = formattedPlate.valid()
          ? formattedPlate.pretty()
          : cleanInput;

        return {
          label: `${displayPlate} ${t("(add new)")}`,
          value: cleanInput,
          __isNew__: true,
        };
      }}
      allowCreateWhileLoading={true}
      createOptionPosition="first"
      defaultOptions={[]}
    />
  );
};
